/*
 * Copyright (c) Microsoft Corporation. All rights reserved. Licensed under the MIT license.
 * See LICENSE in the project root for license information.
 */

/* global document, Office, atob, btoa, Uint8Array, FileReader, window, console */

// Chargés via <script> CDN dans taskpane.html.
declare const pdfjsLib: any;
declare const SignaturePad: any;
declare const PDFLib: any;

const PDF_RENDER_SCALE = 1.5; // résolution d'affichage du canvas (≠ taille affichée, qui est en 100% CSS)

let pdfLoaded = false;
let selected: HTMLElement | null = null;
let signaturePad: any = null;
let clip: HTMLElement | null = null; // presse-papier interne
let originalPdfBase64 = ""; // PDF d'origine (pour pdf-lib ; pdfjs peut "détacher" le buffer)
let accountEmail = ""; // compte Outlook connecté (signature mémorisée par compte)

Office.onReady((info) => {
  if (info.host === Office.HostType.Outlook) {
    document.getElementById("app-body").style.display = "block";
    const profile = Office.context.mailbox.userProfile;
    accountEmail = (profile && profile.emailAddress) || "";
    pdfjsLib.GlobalWorkerOptions.workerSrc = "assets/pdf.worker.min.js";
    wireUi();
    loadFirstPdf();
  } else {
    document.getElementById("sideload-msg").style.display = "block";
  }
});

function wireUi() {
  const fileInput = document.getElementById("add-signature") as HTMLInputElement;
  document.getElementById("add-sig-btn").addEventListener("click", () => fileInput.click());
  fileInput.addEventListener("change", onSignaturePicked);

  document.getElementById("draw-sig-btn").addEventListener("click", openDrawPanel);
  document.getElementById("my-sig-btn").addEventListener("click", applySavedSignature);
  document.getElementById("draw-clear").addEventListener("click", () => signaturePad && signaturePad.clear());
  document.getElementById("draw-cancel").addEventListener("click", closeDrawPanel);
  document.getElementById("draw-ok").addEventListener("click", confirmDraw);

  document.getElementById("add-text-btn").addEventListener("click", addTextOverlay);
  document.getElementById("reply-btn").addEventListener("click", () => sendVia("reply"));
  document.getElementById("replyall-btn").addEventListener("click", () => sendVia("replyAll"));

  document.getElementById("pdf-container").addEventListener("pointerdown", (ev: Event) => {
    if (!(ev.target as HTMLElement).closest(".sig")) selectOverlay(null);
  });

  document.addEventListener("keydown", onKeydown);
}

function isEditingText(): boolean {
  const a = document.activeElement as HTMLElement;
  return !!(a && a.classList && a.classList.contains("txt"));
}

function onKeydown(ev: KeyboardEvent) {
  if (!ev.ctrlKey && !ev.metaKey) return;
  if (isEditingText()) return;
  const k = ev.key.toLowerCase();
  if (k === "c" && selected) {
    clip = selected;
    ev.preventDefault();
  } else if (k === "v" && clip) {
    cloneOverlay(clip);
    ev.preventDefault();
  } else if (k === "d" && selected) {
    cloneOverlay(selected);
    ev.preventDefault();
  }
}

function setStatus(text: string) {
  document.getElementById("status").textContent = text;
}

// --- Lecture + affichage du PDF reçu ---

function loadFirstPdf() {
  const item: any = Office.context.mailbox.item;
  const pdf = (item.attachments || []).find(
    (a: any) => a.contentType === "application/pdf" || /\.pdf$/i.test(a.name)
  );
  if (!pdf) {
    setStatus("Aucun PDF en pièce jointe de ce message.");
    return;
  }
  setStatus(`Chargement de « ${pdf.name} »…`);
  item.getAttachmentContentAsync(pdf.id, (result: Office.AsyncResult<Office.AttachmentContent>) => {
    if (
      result.status !== Office.AsyncResultStatus.Succeeded ||
      result.value.format !== Office.MailboxEnums.AttachmentContentFormat.Base64
    ) {
      setStatus("Impossible de lire la pièce jointe (format non pris en charge).");
      return;
    }
    originalPdfBase64 = result.value.content;
    renderPdf(base64ToBytes(originalPdfBase64), pdf.name);
  });
}

function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) {
    bytes[i] = bin.charCodeAt(i);
  }
  return bytes;
}

async function renderPdf(bytes: Uint8Array, name: string) {
  const container = document.getElementById("pdf-container");
  container.innerHTML = "";
  pdfLoaded = false;
  selected = null;
  try {
    const pdf = await pdfjsLib.getDocument({ data: bytes }).promise;
    for (let n = 1; n <= pdf.numPages; n++) {
      const page = await pdf.getPage(n);
      const viewport = page.getViewport({ scale: PDF_RENDER_SCALE });
      const pageEl = document.createElement("div");
      pageEl.className = "page";
      // dimensions du PDF en points (pour le mapping écran -> PDF à la génération)
      pageEl.dataset.index = String(n - 1);
      pageEl.dataset.pdfw = String(viewport.width / PDF_RENDER_SCALE);
      pageEl.dataset.pdfh = String(viewport.height / PDF_RENDER_SCALE);
      const canvas = document.createElement("canvas");
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      pageEl.appendChild(canvas);
      container.appendChild(pageEl);
      await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
    }
    pdfLoaded = true;
    setStatus(`${name} — ${pdf.numPages} page(s).`);
  } catch (e) {
    setStatus("Erreur d'affichage du PDF.");
    console.error(e);
  }
}

// --- Sélection ---

function selectOverlay(el: HTMLElement | null) {
  if (selected) selected.classList.remove("selected");
  selected = el;
  if (el) el.classList.add("selected");
  if (el && el.classList.contains("text")) positionTextToolbar(el);
}

// Garde la barre flottante du texte dans les limites du conteneur (sinon coupée près des bords).
function positionTextToolbar(wrap: HTMLElement) {
  const bar = wrap.querySelector(".txt-bar") as HTMLElement;
  if (!bar) return;
  const container = document.getElementById("pdf-container") as HTMLElement;
  const barW = bar.offsetWidth;
  const barH = bar.offsetHeight;
  // horizontal : décale vers la gauche si ça dépasse à droite, sans dépasser à gauche
  let left = 0;
  if (wrap.offsetLeft + barW > container.clientWidth) left = container.clientWidth - barW - wrap.offsetLeft;
  if (wrap.offsetLeft + left < 0) left = -wrap.offsetLeft;
  bar.style.left = left + "px";
  // vertical : au-dessus par défaut, en-dessous s'il n'y a pas la place en haut
  if (wrap.offsetTop < barH + 8) {
    bar.style.bottom = "auto";
    bar.style.top = "100%";
    bar.style.marginBottom = "0";
    bar.style.marginTop = "6px";
  } else {
    bar.style.top = "auto";
    bar.style.bottom = "100%";
    bar.style.marginTop = "0";
    bar.style.marginBottom = "6px";
  }
}

function rgbToHex(rgb: string): string {
  const m = rgb.match(/(\d+),\s*(\d+),\s*(\d+)/);
  if (!m) return "#000000";
  const h = (n: string) => ("0" + parseInt(n, 10).toString(16)).slice(-2);
  return "#" + h(m[1]) + h(m[2]) + h(m[3]);
}

function parseRgb(rgb: string): { r: number; g: number; b: number } {
  const m = rgb.match(/(\d+),\s*(\d+),\s*(\d+)/);
  if (!m) return { r: 0, g: 0, b: 0 };
  return { r: +m[1] / 255, g: +m[2] / 255, b: +m[3] / 255 };
}

function newOverlay(extraClass: string): HTMLElement {
  const container = document.getElementById("pdf-container") as HTMLElement;
  const wrap = document.createElement("div");
  wrap.className = "sig" + (extraClass ? " " + extraClass : "");
  const cRect = container.getBoundingClientRect();
  wrap.style.left = "40px";
  wrap.style.top = Math.max(0, window.innerHeight / 2 - cRect.top - 40) + "px";
  return wrap;
}

function addDeleteButton(wrap: HTMLElement) {
  const del = document.createElement("div");
  del.className = "sig-del";
  del.textContent = "×";
  del.title = "Supprimer";
  wrap.appendChild(del); // écouteur posé dans rewireOverlay
}

function rewireOverlay(wrap: HTMLElement) {
  const container = document.getElementById("pdf-container") as HTMLElement;

  if (wrap.classList.contains("text")) {
    const grip = wrap.querySelector(".tt-grip") as HTMLElement;
    const txt = wrap.querySelector(".txt") as HTMLElement;
    // déplacement via la poignée de la barre ; taille via la barre (pas de poignée de coin)
    makeInteractive(wrap, grip, null, container, () => 0, () => {});
    wireTextToolbar(wrap, txt);
  } else {
    const handle = wrap.querySelector(".sig-resize") as HTMLElement;
    const del = wrap.querySelector(".sig-del") as HTMLElement;
    del.addEventListener("click", (ev: Event) => {
      ev.stopPropagation();
      if (selected === wrap) selected = null;
      wrap.remove();
    });
    const save = wrap.querySelector(".sig-save") as HTMLElement;
    if (save) {
      save.addEventListener("click", (ev: Event) => {
        ev.stopPropagation();
        const img = wrap.querySelector("img") as HTMLImageElement;
        if (img) saveAsMySignature(img.src);
      });
    }
    makeInteractive(
      wrap,
      wrap,
      handle,
      container,
      () => ({ w: wrap.offsetWidth, h: wrap.offsetHeight }),
      (dx, dy, s) => {
        wrap.style.width = Math.max(20, s.w + dx) + "px";
        wrap.style.height = Math.max(20, s.h + dy) + "px";
      }
    );
  }
}

// Câble la barre flottante d'un bloc de texte (gras / couleur / taille / suppr) sur SON texte.
function wireTextToolbar(wrap: HTMLElement, txt: HTMLElement) {
  const bold = wrap.querySelector(".tt-bold") as HTMLElement;
  const color = wrap.querySelector(".tt-color") as HTMLInputElement;
  const size = wrap.querySelector(".tt-size") as HTMLInputElement;
  const del = wrap.querySelector(".tt-del") as HTMLElement;

  // init depuis le style courant (gère aussi les clones)
  const cs = window.getComputedStyle(txt);
  bold.classList.toggle("active", cs.fontWeight === "bold" || parseInt(cs.fontWeight, 10) >= 600);
  color.value = rgbToHex(cs.color);
  size.value = String(Math.round(parseFloat(cs.fontSize)));

  bold.addEventListener("click", (ev: Event) => {
    ev.stopPropagation();
    const w = window.getComputedStyle(txt).fontWeight;
    const on = w === "bold" || parseInt(w, 10) >= 600;
    txt.style.fontWeight = on ? "normal" : "700";
    bold.classList.toggle("active", !on);
  });
  color.addEventListener("input", () => {
    txt.style.color = color.value;
  });
  size.addEventListener("input", () => {
    const v = parseInt(size.value, 10);
    if (v >= 8 && v <= 96) txt.style.fontSize = v + "px";
  });
  del.addEventListener("click", (ev: Event) => {
    ev.stopPropagation();
    if (selected === wrap) selected = null;
    wrap.remove();
  });
}

function cloneOverlay(src: HTMLElement) {
  const container = document.getElementById("pdf-container") as HTMLElement;
  const clone = src.cloneNode(true) as HTMLElement;
  clone.classList.remove("selected");
  clone.style.left = parseFloat(src.style.left || "0") + 16 + "px";
  clone.style.top = parseFloat(src.style.top || "0") + 16 + "px";
  container.appendChild(clone);
  rewireOverlay(clone);
  selectOverlay(clone);
}

// --- Signature image (fichier 2a, ou dessin 2b) ---

function onSignaturePicked(e: Event) {
  const input = e.target as HTMLInputElement;
  const file = input.files && input.files[0];
  if (!file) return;
  if (!pdfLoaded) {
    setStatus("Affiche d'abord un PDF avant d'ajouter une signature.");
    return;
  }
  const reader = new FileReader();
  reader.onload = () => addSignatureOverlay(reader.result as string);
  reader.readAsDataURL(file);
  input.value = "";
}

function addSignatureOverlay(dataUrl: string) {
  const container = document.getElementById("pdf-container") as HTMLElement;
  const wrap = newOverlay("");
  wrap.style.width = "160px";

  const img = document.createElement("img");
  img.onload = () => {
    if (!wrap.style.height) {
      wrap.style.height = Math.round(wrap.offsetWidth * (img.naturalHeight / img.naturalWidth)) + "px";
    }
  };
  img.src = dataUrl;
  wrap.appendChild(img);

  const handle = document.createElement("div");
  handle.className = "sig-resize";
  wrap.appendChild(handle);
  addDeleteButton(wrap);

  // ★ mémoriser comme « ma signature » (images uniquement)
  const save = document.createElement("div");
  save.className = "sig-save";
  save.textContent = "★";
  save.title = "Mémoriser comme ma signature";
  wrap.appendChild(save);

  container.appendChild(wrap);
  rewireOverlay(wrap);
  selectOverlay(wrap);
}

// Clé de stockage par compte Outlook connecté.
function sigKey(): string {
  return "leadersign.signature." + (accountEmail || "default");
}

function saveAsMySignature(dataUrl: string) {
  try {
    window.localStorage.setItem(sigKey(), dataUrl);
    setStatus(`Signature mémorisée ✓ pour ${accountEmail || "ce compte"} — réutilise-la via « Ma signature ».`);
  } catch (e) {
    setStatus("Impossible de mémoriser cette signature (image trop lourde ?).");
    console.error(e);
  }
}

function applySavedSignature() {
  if (!pdfLoaded) {
    setStatus("Affiche d'abord un PDF.");
    return;
  }
  // signature du compte connecté ; repli sur l'ancienne clé globale (compatibilité)
  const saved = window.localStorage.getItem(sigKey()) || window.localStorage.getItem("leadersign.signature");
  if (!saved) {
    setStatus(`Aucune signature mémorisée pour ${accountEmail || "ce compte"}. Ajoute/dessine une signature, sélectionne-la, puis clique l'étoile ★.`);
    return;
  }
  addSignatureOverlay(saved);
}

// --- Dessin (2b) : export SVG vectoriel ---

function openDrawPanel() {
  if (!pdfLoaded) {
    setStatus("Affiche d'abord un PDF.");
    return;
  }
  document.getElementById("draw-panel").style.display = "block";
  const canvas = document.getElementById("draw-canvas") as HTMLCanvasElement;
  if (!signaturePad) {
    signaturePad = new SignaturePad(canvas, { penColor: "#000", minWidth: 0.4, maxWidth: 1.5 });
  } else {
    signaturePad.clear();
  }
}

function closeDrawPanel() {
  document.getElementById("draw-panel").style.display = "none";
}

function confirmDraw() {
  if (!signaturePad || signaturePad.isEmpty()) {
    setStatus("Dessine une signature d'abord.");
    return;
  }
  addSignatureOverlay(croppedSvgDataUrl());
  closeDrawPanel();
}

// Recadre le SVG du dessin sur la zone réellement dessinée (sinon l'encre n'est pas centrée dans le cadre).
function croppedSvgDataUrl(): string {
  const full = signaturePad.toDataURL("image/svg+xml");
  let svg = atob(full.split(",")[1]);
  const groups = signaturePad.toData();
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (let i = 0; i < groups.length; i++) {
    const pts = groups[i].points || [];
    for (let j = 0; j < pts.length; j++) {
      minX = Math.min(minX, pts[j].x);
      minY = Math.min(minY, pts[j].y);
      maxX = Math.max(maxX, pts[j].x);
      maxY = Math.max(maxY, pts[j].y);
    }
  }
  if (!isFinite(minX)) return full; // sécurité
  const m = 4; // marge ~ épaisseur du trait
  minX -= m;
  minY -= m;
  maxX += m;
  maxY += m;
  const w = maxX - minX;
  const h = maxY - minY;
  svg = svg.replace(/width="[^"]*"/, `width="${w}"`).replace(/height="[^"]*"/, `height="${h}"`);
  svg = /viewBox="[^"]*"/.test(svg)
    ? svg.replace(/viewBox="[^"]*"/, `viewBox="${minX} ${minY} ${w} ${h}"`)
    : svg.replace("<svg", `<svg viewBox="${minX} ${minY} ${w} ${h}"`);
  return "data:image/svg+xml;base64," + btoa(svg);
}

// --- Texte (2c) ---

function addTextOverlay() {
  if (!pdfLoaded) {
    setStatus("Affiche d'abord un PDF.");
    return;
  }
  const container = document.getElementById("pdf-container") as HTMLElement;
  const wrap = newOverlay("text");

  // barre flottante : poignée + gras + couleur + taille + suppr
  const bar = document.createElement("div");
  bar.className = "txt-bar";
  bar.innerHTML =
    '<span class="tt-grip" title="Déplacer">⠿</span>' +
    '<button type="button" class="tt-bold" title="Gras">G</button>' +
    '<input type="color" class="tt-color" value="#000000" title="Couleur" />' +
    '<input type="number" class="tt-size" min="8" max="96" step="1" title="Taille" />' +
    '<button type="button" class="tt-del" title="Supprimer">×</button>';
  wrap.appendChild(bar);

  const txt = document.createElement("div");
  txt.className = "txt";
  txt.contentEditable = "true";
  txt.textContent = "Texte";
  wrap.appendChild(txt);

  container.appendChild(wrap);
  rewireOverlay(wrap);
  selectOverlay(wrap);

  const range = document.createRange();
  range.selectNodeContents(txt);
  const sel = window.getSelection();
  sel.removeAllRanges();
  sel.addRange(range);
  txt.focus();
}

// --- Étape 3a : aplatir les annotations dans le PDF (pdf-lib) ---

function findPageElFor(ov: HTMLElement): HTMLElement | null {
  const r = ov.getBoundingClientRect();
  const cx = r.left + r.width / 2;
  const cy = r.top + r.height / 2;
  const pages = document.querySelectorAll("#pdf-container .page");
  for (let i = 0; i < pages.length; i++) {
    const pr = (pages[i] as HTMLElement).getBoundingClientRect();
    if (cx >= pr.left && cx <= pr.right && cy >= pr.top && cy <= pr.bottom) return pages[i] as HTMLElement;
  }
  return null;
}

// Rastérise une image (importée ou SVG dessinée) en PNG aux dimensions cibles.
function imgToPngBytes(img: HTMLImageElement, w: number, h: number): Uint8Array {
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  c.getContext("2d").drawImage(img, 0, 0, w, h);
  return base64ToBytes(c.toDataURL("image/png").split(",")[1]);
}

async function generateSignedPdf(): Promise<Uint8Array> {
  const doc = await PDFLib.PDFDocument.load(base64ToBytes(originalPdfBase64));
  const pages = doc.getPages();
  const font = await doc.embedFont(PDFLib.StandardFonts.Helvetica);
  const fontBold = await doc.embedFont(PDFLib.StandardFonts.HelveticaBold);

  const overlays = Array.prototype.slice.call(
    document.querySelectorAll("#pdf-container .sig")
  ) as HTMLElement[];

  for (let i = 0; i < overlays.length; i++) {
    const ov = overlays[i];
    const pageEl = findPageElFor(ov);
    if (!pageEl) continue;
    const pg = pages[parseInt(pageEl.dataset.index, 10)];
    const PW = parseFloat(pageEl.dataset.pdfw);
    const PH = parseFloat(pageEl.dataset.pdfh);
    const pr = pageEl.getBoundingClientRect();
    const or = ov.getBoundingClientRect();
    const sx = PW / pr.width; // points PDF par pixel affiché
    const sy = PH / pr.height;
    const relX = or.left - pr.left;
    const relY = or.top - pr.top;

    if (ov.classList.contains("text")) {
      const txt = ov.querySelector(".txt") as HTMLElement;
      const cs = window.getComputedStyle(txt);
      const sizePts = parseFloat(cs.fontSize) * sy;
      const bold = cs.fontWeight === "bold" || parseInt(cs.fontWeight, 10) >= 600;
      const c = parseRgb(cs.color);
      pg.drawText(txt.textContent || "", {
        x: relX * sx,
        y: PH - relY * sy - sizePts * 0.8, // origine PDF en bas ; ~ascente
        size: sizePts,
        font: bold ? fontBold : font,
        color: PDFLib.rgb(c.r, c.g, c.b),
      });
    } else {
      const img = ov.querySelector("img") as HTMLImageElement;
      const wPts = or.width * sx;
      const hPts = or.height * sy;
      const png = await doc.embedPng(
        imgToPngBytes(img, Math.max(1, Math.round(wPts * 2)), Math.max(1, Math.round(hPts * 2)))
      );
      pg.drawImage(png, { x: relX * sx, y: PH - relY * sy - hPts, width: wPts, height: hPts });
    }
  }
  return doc.save();
}

// --- Étape 3b : renvoyer le PDF signé dans une réponse (PJ base64, Mailbox 1.15) ---

function bytesToBase64(bytes: Uint8Array): string {
  let bin = "";
  const chunk = 0x8000; // découpe pour éviter le dépassement de pile sur un gros PDF
  for (let i = 0; i < bytes.length; i += chunk) {
    bin += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk) as any);
  }
  return btoa(bin);
}

// Répondre / Répondre à tous avec le PDF signé en PJ (base64). Transfert impossible : Office.js
// n'a pas d'API de transfert et le formulaire « nouveau message » n'accepte pas le base64.
async function sendVia(method: "reply" | "replyAll") {
  if (!pdfLoaded) {
    setStatus("Affiche d'abord un PDF.");
    return;
  }
  setStatus("Préparation du document signé…");
  try {
    selectOverlay(null);
    const bytes = await generateSignedPdf(); // aplatit les annotations sur le PDF d'origine
    const formData = {
      htmlBody: "Bonjour,<br><br>Veuillez trouver ci-joint le document signé.<br><br>Cordialement,",
      attachments: [
        {
          type: Office.MailboxEnums.AttachmentType.Base64,
          name: "document-signe.pdf",
          base64file: bytesToBase64(bytes),
          inLine: false,
        },
      ],
    };
    const item: any = Office.context.mailbox.item;
    const open = method === "replyAll" ? item.displayReplyAllFormAsync : item.displayReplyFormAsync;
    open.call(item, formData, {}, (res: Office.AsyncResult<void>) => {
      if (res && res.status === Office.AsyncResultStatus.Failed) {
        setStatus("Échec d'ouverture : " + res.error.message);
      } else {
        setStatus("Fenêtre ouverte avec le PDF signé en pièce jointe — vérifie et envoie.");
      }
    });
  } catch (e) {
    setStatus("Erreur lors de la génération du PDF.");
    console.error(e);
  }
}

// --- Déplacer + redimensionner + sélectionner (générique) ---
function makeInteractive(
  wrap: HTMLElement,
  dragHandle: HTMLElement,
  handle: HTMLElement | null,
  container: HTMLElement,
  getStart: () => any,
  applyResize: (dx: number, dy: number, start: any) => void
) {
  let mode: "" | "move" | "resize" = "";
  let gx = 0;
  let gy = 0;
  let sx = 0;
  let sy = 0;
  let sstart: any = 0;

  wrap.addEventListener("pointerdown", () => selectOverlay(wrap));

  dragHandle.addEventListener("pointerdown", (ev: PointerEvent) => {
    const t = ev.target as HTMLElement;
    if (t.classList && (t.classList.contains("sig-resize") || t.classList.contains("sig-del") || t.classList.contains("sig-save"))) {
      return; // les boutons de contrôle ont leur propre comportement
    }
    mode = "move";
    const r = wrap.getBoundingClientRect();
    gx = ev.clientX - r.left;
    gy = ev.clientY - r.top;
    wrap.setPointerCapture(ev.pointerId);
    ev.preventDefault();
    ev.stopPropagation();
  });

  if (handle) {
    handle.addEventListener("pointerdown", (ev: PointerEvent) => {
      mode = "resize";
      sx = ev.clientX;
      sy = ev.clientY;
      sstart = getStart();
      wrap.setPointerCapture(ev.pointerId);
      ev.preventDefault();
      ev.stopPropagation();
    });
  }

  wrap.addEventListener("pointermove", (ev: PointerEvent) => {
    if (!mode) return;
    if (mode === "resize") {
      applyResize(ev.clientX - sx, ev.clientY - sy, sstart);
      return;
    }
    const r = container.getBoundingClientRect();
    const left = Math.max(0, Math.min(ev.clientX - r.left - gx, container.clientWidth - wrap.offsetWidth));
    const top = Math.max(0, Math.min(ev.clientY - r.top - gy, container.scrollHeight - wrap.offsetHeight));
    wrap.style.left = left + "px";
    wrap.style.top = top + "px";
    if (wrap.querySelector(".txt-bar")) positionTextToolbar(wrap);
  });

  wrap.addEventListener("pointerup", () => {
    mode = "";
  });
}
